// ** React Imports
import { Fragment, lazy } from 'react'
import { Navigate } from 'react-router-dom'
// ** Layouts
import BlankLayout from '@layouts/BlankLayout'
import LayoutWrapper from '@src/@core/layouts/components/layout-wrapper'
import HorizontalLayout from '@src/layouts/HorizontalLayout'
import VerticalLayout from '@src/layouts/VerticalLayout'

// ** Route Components
import PublicRoute from '@components/routes/PublicRoute'

// ** Utils
import { isObjEmpty } from '@utils'
import NavRoutes from '../../common/NavRoutes'

const getLayout = {
  blank: <BlankLayout />,
  vertical: <VerticalLayout />,
  horizontal: <HorizontalLayout />,
}

// ** Document title
const TemplateTitle = '%s - Vuexy React Admin Template'

// ** Default Route
const DefaultRoute = NavRoutes.mapView

const Home = lazy(() => import('../../views/Home'))
const PhaseFlow = lazy(() =>
  import('../../views/junction-parameters/specifications/PhaseFlow')
)
const SignalOutput = lazy(() =>
  import('../../views/junction-parameters/specifications/SignalOutput')
)

const SignalOutputConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/SignalOutputConfig')
)
const PhaseDemand = lazy(() =>
  import('../../views/junction-parameters/specifications/PhaseDemand')
)
const PhaseDemandConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/PhaseDemandConfig')
)
const PushButtonConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/PushButtonConfig')
)
const DemandLoopConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/DemandLoopConfig')
)
const PhaseConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/PhaseConfig')
)
const PhaseConfigSpecification = lazy(() =>
  import(
    '../../views/junction-parameters/specifications/PhaseConfigSpecification'
  )
)
const SystemInputs = lazy(() =>
  import('../../views/junction-parameters/specifications/SystemInputs')
)
const SystemInputsConfig = lazy(() =>
  import('../../views/junction-parameters/specifications/SystemInputsConfig')
)

const SyncFromJunction = lazy(() =>
  import('../../views/junction-parameters/configuration/SyncFromJunction')
)
const UpdateToJunction = lazy(() =>
  import('../../views/junction-parameters/configuration/UpdateToJunction')
)
const SystemGeneralSetting = lazy(() =>
  import(
    '../../views/junction-parameters/junction-control/SystemGeneralSetting'
  )
)
const FaultMonitoring = lazy(() =>
  import('../../views/junction-parameters/junction-control/FaultMonitoring')
)
const GeneralTimeParameters = lazy(() =>
  import(
    '../../views/junction-parameters/junction-control/GeneralTimeParameters'
  )
)
const NightFlashing = lazy(() =>
  import('../../views/junction-parameters/junction-control/NightFlashing')
)

const StreetLight = lazy(() =>
  import('../../views/junction-parameters/junction-control/StreetLight')
)
const AdvanceControlSetting = lazy(() =>
  import(
    '../../views/junction-parameters/junction-control/AdvanceControlSetting'
  )
)
const CountDownMode = lazy(() =>
  import('../../views/junction-parameters/traffic-timer/CountdownMode')
)

const CountdownModeSetting = lazy(() =>
  import('../../views/junction-parameters/traffic-timer/CountdownModeSetting')
)

const XaitsLinkPair = lazy(() =>
  import(
    '../../views/junction-parameters/junction-pairing/xaits-link/TableView'
  )
)
const XaitsLinkPairConfig = lazy(() =>
  import(
    '../../views/junction-parameters/junction-pairing/xaits-link/Configuration'
  )
)
const CountdownSetting = lazy(() =>
  import('../../views/junction-parameters/traffic-timer/CountdownSetting')
)
const GeographicLocation = lazy(() =>
  import('../../views/junction-parameters/geographic-location')
)
const GoGreen = lazy(() =>
  import('../../views/synchronization/parameters/GoGreen')
)
const GoGreenConfig = lazy(() =>
  import('../../views/synchronization/parameters/GoGreenConfig')
)
const ForcePhase = lazy(() =>
  import('../../views/synchronization/parameters/ForcePhase')
)
const ForcePhaseConfig = lazy(() =>
  import('../../views/synchronization/parameters/ForcePhaseConfig')
)
const TimeWindows = lazy(() =>
  import('../../views/synchronization/parameters/TimeWindows')
)
const TransmitForcingSignal = lazy(() =>
  import('../../views/synchronization/parameters/TransmitForcingSignal')
)
const TransmitLockingSignal = lazy(() =>
  import('../../views/synchronization/parameters/TransmitLockingSignal')
)
const TransmitLockingSignalConfig = lazy(() =>
  import('../../views/synchronization/parameters/TransmitLockingSignalConfig')
)
const TransmitForcingSignalConfig = lazy(() =>
  import('../../views/synchronization/parameters/TransmitForcingSignalConfig')
)
const VaTime = lazy(() => import('../../views/time-configuration/va-time'))
const FixTime = lazy(() =>
  import('../../views/time-configuration/multiplan/FixTime')
)
const MaxTime = lazy(() =>
  import('../../views/time-configuration/multiplan/max-time')
)
const MinTime = lazy(() =>
  import('../../views/time-configuration/multiplan/min-time')
)
const GapTime = lazy(() =>
  import('../../views/time-configuration/multiplan/gap-time')
)

const ClockSetting = lazy(() =>
  import('../../views/time-configuration/junction-clock/clock-setting')
)
const ClockSettingConfig = lazy(() =>
  import('../../views/time-configuration/junction-clock/ClockSettingConfig')
)
const OverseeMode = lazy(() =>
  import('../../views/online/live-operation/oversee-mode/index')
)
const Dashboard = lazy(() => import('../../views/Dashboard'))
const MapView = lazy(() => import('../../views/map-view'))
const Rtsp = lazy(() => import('../../views/rtsp'))

const Roles = lazy(() => import('../../views/dashboard/Roles'))
const Permissions = lazy(() => import('../../views/dashboard/Permissions'))
const PhaseAggregation = lazy(() =>
  import('../../views/contemporary-status/mode/phase-aggregation')
)
const TrafficMagnitude = lazy(() =>
  import(
    '../../views/contemporary-status/mode/individual-cycle/traffic-magnitude'
  )
)
const GreenTime = lazy(() =>
  import('../../views/contemporary-status/mode/individual-cycle/green-time')
)
const WaitingTime = lazy(() =>
  import('../../views/contemporary-status/mode/individual-cycle/waiting-time')
)
const CycleComponent = lazy(() =>
  import('../../views/green-time-ledger/CycleComponent')
)
const PhaseComponent = lazy(() =>
  import('../../views/green-time-ledger/PhaseComponent')
)

const AdminControl = lazy(() => import('../../views/admin-control'))

const RecentAlarm = lazy(() => import('../../views/alarms/recent-alarms'))
const AlarmArchive = lazy(() => import('../../views/alarms/alarm-archive'))
const AlertGroup = lazy(() => import('../../views/alarms/alert-group'))
const AlertGroupForm = lazy(() => import('../../views/alarms/alert-group/Form'))

const CyclePerspective = lazy(() =>
  import('../../views/vehicle-volume-ledger/CyclePerspective')
)
const PhasePerspective = lazy(() =>
  import('../../views/vehicle-volume-ledger/PhasePerspective')
)

const JunctionSetting = lazy(() => import('../../views/junction-setting'))
const SecondPage = lazy(() => import('../../views/SecondPage'))
const NotAuthorized = lazy(() => import('../../views/NotAuthorized'))
const Login = lazy(() => import('../../views/Login'))
const Register = lazy(() => import('../../views/Register'))
const ForgotPassword = lazy(() => import('../../views/ForgotPassword'))
const Error = lazy(() => import('../../views/Error'))

// ** Merge Routes
const Routes = [
  {
    path: '/',
    index: true,
    element: <Navigate replace to={DefaultRoute} />,
  },
  {
    path: '/home',
    element: <Home />,
  },
  {
    path: NavRoutes.mapView,
    element: <MapView />,
  },
  {
    path: NavRoutes.phaseFlow,
    element: <PhaseFlow />,
  },
  {
    path: NavRoutes.signalOutput,
    element: <SignalOutput />,
  },
  {
    path: NavRoutes.signalOutputEdit,
    element: <SignalOutputConfig />,
  },
  {
    path: NavRoutes.phaseDemand,
    element: <PhaseDemand />,
  },
  {
    path: NavRoutes.phaseDemandConfig,
    element: <PhaseDemandConfig />,
  },
  {
    path: NavRoutes.pushButtonConfig,
    element: <PushButtonConfig />,
  },
  {
    path: NavRoutes.demandLoopConfig,
    element: <DemandLoopConfig />,
  },
  {
    path: NavRoutes.phaseConfig,
    element: <PhaseConfig />,
  },
  {
    path: NavRoutes.phaseConfigSpecification,
    element: <PhaseConfigSpecification />,
  },
  {
    path: NavRoutes.systemInputs,
    element: <SystemInputs />,
  },
  {
    path: NavRoutes.systemInputsConfig,
    element: <SystemInputsConfig />,
  },
  {
    path: NavRoutes.syncFromJunction,
    element: <SyncFromJunction />,
  },
  {
    path: NavRoutes.updateToJunction,
    element: <UpdateToJunction />,
  },
  {
    path: NavRoutes.systemGeneralSetting,
    element: <SystemGeneralSetting />,
  },
  {
    path: NavRoutes.faultMonitoring,
    element: <FaultMonitoring />,
  },
  {
    path: NavRoutes.generalTimeParameters,
    element: <GeneralTimeParameters />,
  },
  {
    path: NavRoutes.nightFlashing,
    element: <NightFlashing />,
  },
  {
    path: NavRoutes.streetLight,
    element: <StreetLight />,
  },
  {
    path: NavRoutes.advanceControlSetting,
    element: <AdvanceControlSetting />,
  },
  {
    path: NavRoutes.countdownMode,
    element: <CountDownMode />,
  },
  {
    path: NavRoutes.countdownModeSetting,
    element: <CountdownModeSetting />,
  },
  {
    path: NavRoutes.xaitsLinkPair,
    element: <XaitsLinkPair />,
  },
  {
    path: NavRoutes.xaitsLinkPairConfig,
    element: <XaitsLinkPairConfig />,
  },
  {
    path: NavRoutes.countdownSetting,
    element: <CountdownSetting />,
  },
  {
    path: NavRoutes.geographicLocation,
    element: <GeographicLocation />,
  },
  {
    path: NavRoutes.rtsp,
    element: <Rtsp />,
  },
  {
    path: NavRoutes.goGreen,
    element: <GoGreen />,
  },
  {
    path: NavRoutes.goGreenConfig,
    element: <GoGreenConfig />,
  },
  {
    path: NavRoutes.forcePhase,
    element: <ForcePhase />,
  },
  {
    path: NavRoutes.forcePhaseConfig,
    element: <ForcePhaseConfig />,
  },
  {
    path: NavRoutes.timeWindows,
    element: <TimeWindows />,
  },
  {
    path: NavRoutes.transmitForcingSignal,
    element: <TransmitForcingSignal />,
  },
  {
    path: NavRoutes.transmitLockingSignal,
    element: <TransmitLockingSignal />,
  },
  {
    path: NavRoutes.transmitLockingSignalConfig,
    element: <TransmitLockingSignalConfig />,
  },
  {
    path: NavRoutes.transmitForcingSignalConfig,
    element: <TransmitForcingSignalConfig />,
  },
  {
    path: NavRoutes.vaTime,
    element: <VaTime />,
  },
  {
    path: NavRoutes.fixTime,
    element: <FixTime />,
  },
  {
    path: NavRoutes.maxTime,
    element: <MaxTime />,
  },
  {
    path: NavRoutes.minTime,
    element: <MinTime />,
  },
  {
    path: NavRoutes.gapTime,
    element: <GapTime />,
  },
  {
    path: NavRoutes.clockSetting,
    element: <ClockSetting />,
  },
  {
    path: NavRoutes.clockSettingConfig,
    element: <ClockSettingConfig />,
  },
  {
    path: NavRoutes.overseeMode,
    element: <OverseeMode />,
  },
  {
    path: NavRoutes.dashboard,
    element: <Dashboard />,
  },
  {
    path: NavRoutes.roles,
    element: <Roles />,
  },
  {
    path: NavRoutes.permissions,
    element: <Permissions />,
  },
  {
    path: NavRoutes.phaseAggregation,
    element: <PhaseAggregation />,
  },
  {
    path: NavRoutes.trafficMagnitude,
    element: <TrafficMagnitude />,
  },
  {
    path: NavRoutes.greenTime,
    element: <GreenTime />,
  },
  {
    path: NavRoutes.waitingTime,
    element: <WaitingTime />,
  },
  {
    path: NavRoutes.greenTimeLedgerPhase,
    element: <PhaseComponent />,
  },
  {
    path: NavRoutes.greenTimeLedgerCycle,
    element: <CycleComponent />,
  },
  {
    path: NavRoutes.recentAlarm,
    element: <RecentAlarm />,
  },
  {
    path: NavRoutes.alarmArchive,
    element: <AlarmArchive />,
  },
  {
    path: NavRoutes.alertGroup,
    element: <AlertGroup />,
  },
  {
    path: NavRoutes.alertGroupForm,
    element: <AlertGroupForm />,
  },
  {
    path: `${NavRoutes.alertGroupForm}/:alertGroupId`,
    element: <AlertGroupForm />,
  },

  {
    path: NavRoutes.vehicleVolumeLedgerPhase,
    element: <PhasePerspective />,
  },
  {
    path: NavRoutes.vehicleVolumeLedgerCycle,
    element: <CyclePerspective />,
  },
  {
    path: NavRoutes.adminControl,
    element: <AdminControl />,
    meta: {
      menuHidden: true,
    },
  },
  {
    path: '/second-page',
    element: <SecondPage />,
  },
  {
    path: NavRoutes.junctionSetting,
    element: <JunctionSetting />,
  },
  {
    path: '/not-authorized',
    element: <NotAuthorized />,
  },
  {
    path: '/login',
    element: <Login />,
    meta: {
      layout: 'blank',
    },
  },
  {
    path: '/register',
    element: <Register />,
    meta: {
      layout: 'blank',
    },
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
    meta: {
      layout: 'blank',
    },
  },
  {
    path: '/error',
    element: <Error />,
    meta: {
      layout: 'blank',
    },
  },
]

const getRouteMeta = (route) => {
  if (isObjEmpty(route.element.props)) {
    if (route.meta) {
      return { routeMeta: route.meta }
    } else {
      return {}
    }
  }
}

// ** Return Filtered Array of Routes & Paths
const MergeLayoutRoutes = (layout, defaultLayout) => {
  const LayoutRoutes = []

  if (Routes) {
    Routes.filter((route) => {
      let isBlank = false
      // ** Checks if Route layout or Default layout matches current layout
      if (
        (route.meta && route.meta.layout && route.meta.layout === layout) ||
        ((route.meta === undefined || route.meta.layout === undefined) &&
          defaultLayout === layout)
      ) {
        const RouteTag = PublicRoute

        // ** Check for public or private route
        if (route.meta) {
          route.meta.layout === 'blank' ? (isBlank = true) : (isBlank = false)
        }
        if (route.element) {
          const Wrapper =
            // eslint-disable-next-line multiline-ternary
            isObjEmpty(route.element.props) && isBlank === false
              ? // eslint-disable-next-line multiline-ternary
                LayoutWrapper
              : Fragment

          route.element = (
            <Wrapper {...(isBlank === false ? getRouteMeta(route) : {})}>
              <RouteTag route={route}>{route.element}</RouteTag>
            </Wrapper>
          )
        }

        // Push route to LayoutRoutes
        LayoutRoutes.push(route)
      }
      return LayoutRoutes
    })
  }
  return LayoutRoutes
}

const getRoutes = (layout) => {
  const defaultLayout = layout || 'vertical'
  const layouts = ['vertical', 'horizontal', 'blank']

  const AllRoutes = []

  layouts.forEach((layoutItem) => {
    const LayoutRoutes = MergeLayoutRoutes(layoutItem, defaultLayout)

    AllRoutes.push({
      path: '/',
      element: getLayout[layoutItem] || getLayout[defaultLayout],
      children: LayoutRoutes,
    })
  })
  return AllRoutes
}

export { DefaultRoute, Routes, TemplateTitle, getRoutes }
